import { useMemo, useState } from 'react';

import { ActionText } from '@components/action-text';
import { Breadcrumb } from '@components/breadcrumb';
import { EmptySearchState } from '@components/empty-search-state';
import { EmptyState } from '@components/empty-state';
import { ExpandableSearch } from '@components/expandable-search';
import { useConfirmModal } from '@components/generic-modals';
import { Header } from '@components/header';
import { IconButton } from '@components/icon-button';
import { IconCloudSync } from '@components/icons/cloud-sync';
import { IconCloudUnsync } from '@components/icons/cloud-unsync';
import { IconDate } from '@components/icons/date';
import { IconDropdown } from '@components/icons/dropdown';
import { IconEdit } from '@components/icons/edit';
import { IconList } from '@components/icons/list';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconPerson } from '@components/icons/person';
import { IconReplace } from '@components/icons/replace';
import { IconTextFormatNumber } from '@components/icons/text-format-number';
import { IconTrash } from '@components/icons/trash';
import { IconUpload } from '@components/icons/upload';
import { List } from '@components/integrations/integrations-types';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { PageContent } from '@components/page-containers/page-container';
import { Stack } from '@components/stack';
import { FilterConfig, Table, TableProps } from '@components/table';
import { usePersistedTableStore } from '@components/table/table-store';
import { AvailableColumn } from '@components/table/types';
import { Tag } from '@components/tag';
import { Text } from '@components/text';
import { showError } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';
import { toQueryString } from '@utils/requests';

import { useCurrentCompany, useTableView } from 'api/authentication/hooks';
import { useLists, useSyncToManualList } from 'api/lists/hooks';

import { useImportListModal } from './import';
import { useListSidebar } from './sidebar';
import { useSyncListSidebar } from './sync-list-sidebar';

import { useDeleteListModal } from '../modals/list-delete';

export const CompanyLists = () => {
  const { t } = useScopedTranslate(locale);
  const [search, setSearch] = useState('');

  const queryString = toQueryString({
    search: search,
  });

  const { data, isLoading } = useLists(queryString);

  if (isLoading) return <Spinner />;

  return (
    <>
      <Header
        actions={<Actions search={search} setSearch={setSearch} lists={data ?? []} />}
        breadcrumbs={<Breadcrumb items={[{ label: t('lists') }]} />}
      />
      <ListPageContent lists={data} search={search} />
    </>
  );
};

const ListPageContent = ({ search, lists }: { search: string; lists?: List[] }) => {
  return (
    <PageContent noPadding>
      <ListTable search={search} lists={lists} />
    </PageContent>
  );
};

const RenderListTag = ({ list }: { list: List }) => {
  const { t } = useScopedTranslate(locale);
  if (list.listType !== 'synced') {
    return <Tag text={t('manual')} variant="blue" size="large" />;
  }
  if (list.sync?.hasError) {
    return <Tag text={t('error')} variant="red" size="large" icon={<IconCloudUnsync />} />;
  }
  if (list.sync?.isActive) {
    return <Tag text={t('synced')} variant="green" size="large" icon={<IconCloudSync />} />;
  }
  return <Tag text={t('disabled')} variant="gray" size="large" icon={<IconCloudSync />} />;
};

const ListTable = ({ search, lists }: { search: string; lists?: List[] }) => {
  const { t } = useScopedTranslate(locale);
  const showSidebar = useListSidebar();
  const showDeleteModal = useDeleteListModal();
  const showConfirmModal = useConfirmModal('LARGE');
  const changeSyncListMutation = useSyncToManualList();

  const isDataEmpty = !lists || lists.length === 0;

  const { tableView, setTableView } = useTableView('lists');
  const tableStore = usePersistedTableStore({
    tableView,
    setTableView,
  });

  const availableColumns: Array<AvailableColumn<List>> = useMemo(
    () => [
      {
        id: 'name',
        title: t('name'),
        type: 'text',
        Cell: ({ row: list }) => (
          <Stack space={2} orientation="horizontal" align="center">
            <ActionText
              font="regular14"
              color="textPrimary"
              onClick={() => showSidebar({ listId: list.shortId })}
            >
              {list.name}
            </ActionText>
          </Stack>
        ),
      },
      {
        id: 'listType',
        title: t('listType'),
        type: 'text',
        Cell: ({ row: list }) => <RenderListTag list={list} />,
      },
      {
        id: 'source',
        title: t('source'),
        type: 'text',
        renderFallback: () => {
          return <Text text={t('noSource')} font="regular14" color="textHelper" />;
        },
      },
      {
        id: 'dateCreated',
        title: t('created'),
        type: 'date',
      },
      {
        id: 'numOptions',
        title: t('numOptions'),
        type: 'number',
      },
    ],
    [showSidebar, t]
  );

  const endFixedColumns: TableProps<List>['endFixedColumns'] = useMemo(
    () => [
      {
        id: 'options',
        title: '',
        type: 'custom',
        Cell: ({ row: list }) => (
          <Menu
            trigger={IconButton}
            triggerProps={{
              variant: 'ghost-purple',
              size: 'md',
              icon: <IconMoreVertical />,
            }}
          >
            <Menu.Item
              label={t('core.edit')}
              icon={<IconEdit />}
              onSelect={() => showSidebar({ listId: list.shortId })}
            />
            {list.listType === 'synced' && (
              <Menu.Item
                label={t('changeToManual')}
                icon={<IconReplace />}
                onSelect={() => {
                  showConfirmModal({
                    title: t('changeTitle'),
                    description: t('changeModalText'),
                    buttonText: t('changeButton'),
                    buttonIcon: <IconReplace />,
                    onConfirm: () => {
                      if (list.sync) {
                        changeSyncListMutation.mutateAsync(list.sync.shortId);
                      } else {
                        showError(t('couldNotChange'));
                      }
                    },
                  });
                }}
              />
            )}
            <Menu.Item
              label={t('core.delete')}
              icon={<IconTrash />}
              variant="danger"
              onSelect={() => showDeleteModal({ list })}
            />
          </Menu>
        ),
      },
    ],
    [changeSyncListMutation, showConfirmModal, showDeleteModal, showSidebar, t]
  );

  const filterConfig: FilterConfig = [
    { id: 'name', type: 'text', icon: <IconPerson />, label: t('name') },
    {
      id: 'listType',
      type: 'single_select',
      icon: <IconList />,
      label: t('listType'),
      values: [
        { value: 'manual', label: t('manual') },
        { value: 'synced', label: t('synced') },
      ],
    },
    { id: 'source', type: 'text', icon: <IconList />, label: t('source') },
    { id: 'dateCreated', type: 'date', icon: <IconDate />, label: t('created') },
    { id: 'numOptions', type: 'number', icon: <IconTextFormatNumber />, label: t('numOptions') },
  ];

  if (isDataEmpty) {
    return search ? (
      <EmptySearchState />
    ) : (
      <EmptyState
        icon={<IconList size={80} />}
        header={t('noLists')}
        content={t('noListsContent')}
      />
    );
  }

  return (
    <Table<List>
      idColumn="shortId"
      searchStr={search}
      data={lists}
      filterProps={{ config: filterConfig, addFilterText: t('core.filter') }}
      availableColumns={availableColumns}
      endFixedColumns={endFixedColumns}
      store={tableStore}
    />
  );
};

const Actions = ({
  search,
  setSearch,
  lists,
}: {
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  lists: List[];
}) => {
  const { t } = useScopedTranslate(locale);
  const company = useCurrentCompany();
  const showSidebar = useListSidebar();
  const showImportModal = useImportListModal();
  const showSyncListSidebar = useSyncListSidebar();

  const hasIntegrations = company?.hasIntegrations ?? false;

  return (
    <Stack space={2} orientation="horizontal">
      <ExpandableSearch onChange={setSearch} defaultValue={search} />
      <Menu
        buttonProps={{
          variant: 'primary',
          iconLeft: <IconList />,
          text: t('createList'),
          iconRight: <IconDropdown />,
        }}
      >
        <Menu.Item label={t('createNewList')} onSelect={() => showSidebar()} icon={<IconList />} />
        <Menu.Item
          label={t('importList')}
          onSelect={() => showImportModal({ lists })}
          icon={<IconUpload />}
        />
        <Menu.Item
          label={t('addSyncedList')}
          onSelect={() => showSyncListSidebar()}
          icon={<IconCloudSync />}
          isDisabled={!hasIntegrations}
          tooltip={!hasIntegrations ? t('noIntegrations') : undefined}
        />
      </Menu>
    </Stack>
  );
};

const locale = {
  en: {
    lists: 'Lists',
    name: 'Name',
    source: 'Source',
    numOptions: '# Options',
    createList: 'Create list',
    createNewList: 'Create a new list',
    importList: 'Import list • CSV',
    companyLists: 'Company lists',
    addSyncedList: 'Sync list • Integration',
    noSource: 'No source',
    noLists: 'No lists',
    noListsContent:
      'You haven’t created any lists yet. Create and maintain source lists that can be accessed and used in various parts of the system',
    noIntegrations: 'This features requires active integrations to be set up',
    listType: 'List type',
    created: 'Created',
    synced: 'Synced',
    manual: 'Manual',
    disabled: 'Disabled',
    error: 'Error',
    changeToManual: 'Change to a manual list',
    changeTitle: 'Change synced list to a manual list',
    changeModalText:
      'You are about to change a synced list to a manual list.\nThis will remove the sync settings and the list will no longer be updated automatically.\n\nAre you sure you want to proceed?',
    changeButton: 'Change to manual',
    couldNotChange: 'Could not change list to manual',
  },
};
